import type { ClikArtItem } from '@/api/types/clikart'
import type { CandidDetail } from '@/api/types/photo'
import {
  clikArtPreviewPatch,
  shapeNeedsClikArtPreview,
} from '@/lib/clikart/resolve-clikart-preview'
import { resolvePlacedClikArtId } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import {
  imageIdNeedingResolution,
  imagePreviewPatchFromCandidDetail,
  shapeNeedsImagePreviewResolution,
} from '@/lib/photos/resolve-missing-image-preview'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { logger } from '@/utils/logger'

export type ResolvePlacedImagePreviewsDeps = {
  /** One ClikArt by id; null when the id is unknown. */
  getClikArtById: (artId: string) => Promise<ClikArtItem | null>
  getCandidDetail: (candidId: string) => Promise<CandidDetail>
}

/**
 * ClikArt already looked up this session, by art id. A template drop can look up the same art more
 * than once (a retry, or the same template placed again), and an art's URL does not change.
 */
const clikArtById = new Map<string, ClikArtItem>()

/** Forgets every ClikArt looked up so far. Only tests call it: the cache is per page load. */
export function clearResolvedClikArtPreviews(): void {
  clikArtById.clear()
}

async function lookUp<T>(
  ids: Iterable<string>,
  fetchOne: (id: string) => Promise<T | null>,
  what: string
): Promise<Map<string, T>> {
  const found = new Map<string, T>()

  await Promise.all(
    [...ids].map(async (id) => {
      try {
        const item = await fetchOne(id)

        if (item) found.set(id, item)
      } catch (error) {
        logger.warn(`Failed to look up ${what} ${id} for its preview`, error)
      }
    })
  )

  return found
}

async function lookUpClikArt(
  artIds: Set<string>,
  getClikArtById: ResolvePlacedImagePreviewsDeps['getClikArtById']
): Promise<Map<string, ClikArtItem>> {
  const found = await lookUp(
    [...artIds].filter((id) => !clikArtById.has(id)),
    getClikArtById,
    'ClikArt'
  )

  for (const [id, item] of found) {
    clikArtById.set(id, item)
  }

  const resolved = new Map<string, ClikArtItem>()

  for (const id of artIds) {
    const item = clikArtById.get(id)

    if (item) resolved.set(id, item)
  }

  return resolved
}

/**
 * Gives the placed shapes that carry an image id but nothing to draw their preview URL, so they
 * show on the canvas the moment they are added.
 *
 * A template is decomposed from an NSD, which never stores a preview URL (it is editor-only on the
 * wire), so its ClikArt and photos arrive as ids. A ClikArt is looked up by art id and a photo by
 * candid id, once per distinct id and in parallel. Shapes with a preview, or with an id that can't
 * be looked up, come back as they were: the lookup is best effort and never rejects, so a failed
 * one leaves an empty frame rather than blocking the placement. The load-time backfill
 * (`useResolveMissingImagePreviews`) tries the same shapes again on the next load.
 */
export async function resolvePlacedImagePreviews(
  shapes: ShapeData[],
  { getClikArtById, getCandidDetail }: ResolvePlacedImagePreviewsDeps
): Promise<ShapeData[]> {
  const artIds = new Set<string>()
  const candidIds = new Set<string>()

  for (const shape of shapes) {
    if (shapeNeedsClikArtPreview(shape)) {
      artIds.add(resolvePlacedClikArtId(shape) as string)
    } else if (shapeNeedsImagePreviewResolution(shape)) {
      candidIds.add(imageIdNeedingResolution(shape) as string)
    }
  }

  if (artIds.size === 0 && candidIds.size === 0) return shapes

  const [arts, candids] = await Promise.all([
    lookUpClikArt(artIds, getClikArtById),
    lookUp(candidIds, getCandidDetail, 'photo'),
  ])

  return shapes.map((shape) => {
    const art = shapeNeedsClikArtPreview(shape)
      ? arts.get(resolvePlacedClikArtId(shape) as string)
      : undefined
    const candid = shapeNeedsImagePreviewResolution(shape)
      ? candids.get(imageIdNeedingResolution(shape) as string)
      : undefined
    const patch = art
      ? clikArtPreviewPatch(shape, art)
      : candid
        ? imagePreviewPatchFromCandidDetail(shape, candid)
        : null

    return patch ? { ...shape, ...patch } : shape
  })
}
